/**
 * `approvals.*` — founder approvals inbox.
 *
 * V1.0.x exposes `request`, `decide`, `list`. The decide path is gated to
 * Founder role by the domain (membership lookup); the request path has no
 * role gate (any tenant member can file an approval).
 */
import { z } from "zod";
import { uuid, paged } from "../_common.js";

export const approvalKind = z.enum([
  // ── ADR-0031 action types (the engine path; `kind` is the action type) ──
  "vendor_payment",
  "payroll_run",
  "purchase_order",
  "vendor_bill",
  "goods_receipt",
  "invoice_void",
  "invoice_discount",
  "stock_writeoff",
  // ── Legacy kinds (pre-ADR-0031 rows + un-migrated gates) ──
  "void_invoice",
  "over_discount_invoice",
  "over_credit_limit_invoice",
  "write_off_invoice",
  "automated_send",
  /**
   * Operator-initiated vendor payment ≥ per-Company threshold
   * (system-decisions §2 / persona-journeys F11). The `requestPayload`
   * carries the validated `vendorPayments.recordInput`. On founder approve,
   * the operator re-submits `vendorPayments.record` with `approvalId` set;
   * the domain verifies the deep-equal of the payload and bypasses the
   * threshold check. The `vendor_payments` row is inserted only at that
   * second attempt (the table is INSERT-only — RLS doesn't grant UPDATE).
   */
  "vendor_payment_above_threshold",
  /**
   * Payroll Increment 2 — a payroll run whose total net ≥ the tenant's
   * approval threshold. The `requestPayload` carries `{ runId, periodMonth,
   * netPaise, employeeCount }`. On founder approve, the operator re-submits
   * `payrollRuns.submit` with `approvalId` set; the domain verifies the run +
   * net still match and advances the run to `approved`. The run row stays
   * `draft` until then (payroll_runs grants UPDATE, so no extra insert).
   */
  "payroll_run_above_threshold",
]);

export const approvalStatus = z.enum(["pending", "approved", "rejected"]);

// ── request (any tenant member) ──────────────────────────────────
export const requestInput = z.object({
  kind: approvalKind,
  summary: z.string().trim().min(3).max(255),
  entityType: z.string().trim().min(1).max(32),
  entityId: uuid,
  requestPayload: z.record(z.string(), z.unknown()).optional(),
});

export const requestOutput = z.object({ id: uuid });

// ── decide (Founder only — domain enforces) ──────────────────────
export const decideInput = z.object({
  id: uuid,
  decision: z.enum(["approved", "rejected"]),
  /** Optional note — printed on the audit row + on the inbox card history. */
  note: z.string().trim().max(500).optional(),
});

/**
 * The result of one decision. For multi-stage chains (ADR-0031) a single
 * decision may not finish the request: `outcome` distinguishes a recorded vote
 * that advanced/awaits more steps from a terminal decision. `status` is the
 * overall row status (still `pending` until the final stage clears);
 * `decidedAt` is set only on the terminal decision.
 */
export const decideOutput = z.object({
  id: uuid,
  outcome: z.enum(["pending", "stage_advanced", "approved", "rejected"]),
  status: approvalStatus,
  decidedAt: z.string().datetime().nullable(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  /** Defaults to pending (the inbox view). */
  status: approvalStatus.optional(),
});

const listRow = z.object({
  id: uuid,
  kind: approvalKind,
  summary: z.string(),
  entityType: z.string(),
  entityId: uuid,
  requestPayload: z.record(z.string(), z.unknown()),
  status: approvalStatus,
  requestedByActorId: z.string(),
  requestedByActorType: z.string(),
  decidedByUserName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  decidedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow).extend({
  /** Drives the sidebar badge — count of pending rows tenant-wide. */
  pendingCount: z.number().int().nonnegative(),
});

// ── derived TS types ──────────────────────────────────────────────
export type RequestInput = z.infer<typeof requestInput>;
export type RequestOutput = z.infer<typeof requestOutput>;
export type DecideInput = z.infer<typeof decideInput>;
export type DecideOutput = z.infer<typeof decideOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type ApprovalKind = z.infer<typeof approvalKind>;
export type ApprovalStatus = z.infer<typeof approvalStatus>;
